<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { formatHelpers, viewportSize, LoadingView, PrimaryButton, VIEWPORT_NAMES, AllowedRoundedSize } from '@hfc/ui-core';
import { Detail } from '@/planList/domain/Detail';
import { PlanResponse } from '@/planList/domain/PlanResponse';
import { useCarrierInfo } from '@/application/shared/infrastructure/composition/useCarrierInfo';
import { useComparePlansStore } from '@/shared/infrastructure/composition/useComparePlansStore';

const props = withDefaults(defineProps<{
  showApplyButton?: boolean
  showViewDetailsLink?: boolean
  selectable?: boolean
  priceAttribute?: string
  priceLabel?: string
}>(), {
  priceAttribute: 'rate',
  priceLabel: 'month'
});

interface emitInterface {
  (event: 'openComparison'): void;
  (event: 'closeComparison'): void;
  (event: 'applyPlan', reference_id: string): void;
  (event: 'removePlan', reference_id: string): void;
  (event: 'viewDetails', reference_id: string): void;
  (event: 'highlightPlan', reference_id: string): void;
}
const emits = defineEmits<emitInterface>();

const { viewport } = viewportSize();
const { formatPrice } = formatHelpers();
const { getCarrierLogoId } = useCarrierInfo();

const { comparePlansStore, comparePlansState } = useComparePlansStore();

const highlightedPlan = ref<string | null>(null);

const mappedPlansDescription = computed(() => {
  const keys = new Map();
  comparePlansState.value.plansToCompare.flatMap((plan) =>{
    plan.details.map((detail) => {
      keys.set(detail.title, { key: detail.title, isTitle: detail.description === '' });
    });
  });

  return Array.from(keys.values());
});

const applyButtonSize = computed(() => viewport.value.name === VIEWPORT_NAMES.ALL ? 'sm' : 'md');

const getValueFromPlanKey = (key: string, planDetails: Array<Detail>): string => {
  return planDetails.find((detail) => detail.title === key)?.description || '-';
};

const toggleComparisonModal = (): void => {
  comparePlansStore.changeComparisonModalState();
  if (comparePlansState.value.openComparisonModal) {
    emits('openComparison');
    if (props.selectable) {
      setHighlightedPlan(comparePlansState.value.plansToCompare[0].reference_id);
    }
  }
  checkBodyOverflow();
};

const closeComparison = (): void => {
  comparePlansStore.emptyPlansToCompare();
  if (comparePlansState.value.openComparisonModal) {
    comparePlansStore.changeComparisonModalState();
  }
  checkBodyOverflow();
  emits('closeComparison');
};

const applyPlan = (reference_id: string): void => {
  emits('applyPlan', reference_id);
  closeComparison();
};

const viewDetails = (reference_id: string): void => {
  emits('viewDetails', reference_id);
};

const removePlan = (reference_id: string): void => {
  comparePlansStore.removePlanToCompare(reference_id);

  if (comparePlansState.value.plansToCompare.length <= 1) {
    checkBodyOverflow();
  }

  emits('removePlan', reference_id);
};

const checkBodyOverflow = (): void => {
  if (comparePlansState.value.openComparisonModal) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
};

const setHighlightedPlan = (reference_id: string): void => {
  if (props.selectable) {
    highlightedPlan.value = reference_id;
    emits('highlightPlan', reference_id);
  }
};

const getPlanPrice = (plan: object): string => {
  const record = plan as Record<string, unknown>;
  const priceToShow = props.priceAttribute in record ? record[props.priceAttribute] : record.rate;

  return formatPrice(priceToShow as number);
};

onMounted(() => {
  comparePlansStore.setPlansMaxLimit(viewport.value.name);
});

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-hidden');
});
</script>
<template>
  <div v-highlight v-if="comparePlansState.plansToCompare.length > 0" class="flex justify-center items-center fixed bottom-0 left-0 w-full transition-all duration-300 ease-in-out z-50" :class="comparePlansState.openComparisonModal ? 'h-full bg-black bg-opacity-50' : ''">
    <div class="flex w-full bg-white border px-2 py-2 transition-all duration-300 ease-in-out" :class="{ 'flex-col rounded-lg h-[calc(100vh-32px)] m-4 md:h-4/5 md:mt-12 md:mx-10 md:mb-0': comparePlansState.openComparisonModal, 'flex-col sm:flex-row': !comparePlansState.openComparisonModal }">
      <div v-if="!comparePlansState.openComparisonModal" class="flex items-center justify-center lg:w-52 flex-col m-2">
        <span class="text-sm lg:text-md font-semibold">Compare</span>
        <span class="text-sm lg:text-md text-wrap">(up to {{ comparePlansState.plansLimit }} plans)</span>
      </div>
      <div class="w-full overflow-x-auto" :class="{ 'h-full': comparePlansState.loadingPlansToCompare && comparePlansState.openComparisonModal }">
        <slot name="above-table" :modal-open="comparePlansState.openComparisonModal"></slot>
        <table class="table-fixed w-full h-full">
          <thead class="sticky top-0 bg-white">
            <tr :class="{ 'flex justify-center': !comparePlansState.openComparisonModal }">
              <th v-if="comparePlansState.openComparisonModal" class="md:w-48">
                <div class="h-full flex items-center justify-center border border-blue-500 text-blue-500 rounded-md">
                  <span class="flex-grow text-wrap text-sm lg:text-xl">
                    {{ comparePlansState.plansToCompare.length }} Plans selected for comparison
                  </span>
                </div>
              </th>
              <th v-for="plan in comparePlansState.plansToCompare" :key="plan.id" class="relative" :class="{ 'w-36 lg:w-52': !comparePlansState.openComparisonModal, 'flex-1': comparePlansState.openComparisonModal, 'cursor-pointer': selectable && comparePlansState.openComparisonModal}" @click="setHighlightedPlan(plan.reference_id)">
                <div class="flex h-full w-full flex-col border border-blue-500 text-blue-500 rounded-md" :class="{ 'border-2 border-green-500': highlightedPlan === plan.reference_id }">
                  <button class="flex absolute right-1 top-1 border shadow-lg border-blue-400 rounded-full px-1.5 py-1 bg-blue-500 text-white hover:bg-blue-400" @click="removePlan(plan.reference_id)">
                    <hfc-icon class="text-sm lg:text-md" icon="close" />
                  </button>
                  <div class="flex lg:h-20 px-2 py-1 lg:px-4 lg:py-3 justify-center items-center">
                    <carrier-logo custom-class="max-h-10 lg:max-h-16 max-w-full" :carrier-id="getCarrierLogoId(plan as PlanResponse)" vertical="true" no-link="true" />
                  </div>
                  <div class="flex flex-grow items-center justify-center py-2 px-4">
                    <p class="text-blue text-wrap font-semibold text-xs lg:text-sm lining-nums">{{ plan.name }}</p>
                  </div>
                  <div class="flex flex-col md:flex-row justify-center items-center gap-1 sm:gap-2 py-2">
                    <button v-if="showViewDetailsLink && comparePlansState.openComparisonModal" @click="viewDetails(plan.reference_id)" class="text-blue uppercase text-sm font-normal scale-90 xs:scale-100"><span class="hidden xs:inline-block">View</span>&nbsp;Details</button>
                    <PrimaryButton class="scale-90 xs:scale-100" v-if="showApplyButton && comparePlansState.openComparisonModal" :size="applyButtonSize" :rounded="AllowedRoundedSize.BASIC" @click="applyPlan(plan.reference_id)">Apply&nbsp;<span class="hidden xs:inline-block">Now</span></PrimaryButton>
                  </div>
                  <div class="flex flex-col xl:flex-row items-center justify-center object-cover bg-blue lg:py-2 text-slate-200">
                    <span class="text-sm lg:text-lg font-semibold">{{ getPlanPrice(plan) }}<span class="text-xs">/ {{ priceLabel }}</span></span>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody v-if="comparePlansState.openComparisonModal && !comparePlansState.loadingPlansToCompare">
            <template v-for="planDetails in mappedPlansDescription" :key="planDetails.section">
              <tr v-if="planDetails.isTitle">
                <td :colspan="comparePlansState.plansToCompare.length + 1" class="px-4 py-1 bg-gray-400 text-white rounded-md compare-plans-row-title">{{ planDetails.key }}</td>
              </tr>
              <tr v-else class="text-xs lg:text-sm even:bg-gray-50 odd:bg-gray-200">
                <td class="pl-4 py-1">{{ planDetails.key}}</td>
                <td class="text-right last:pr-4" v-for="plan in comparePlansState.plansToCompare" :key="plan.id" v-html="getValueFromPlanKey(planDetails.key, plan.details as Detail[])"></td>
              </tr>
            </template>
          </tbody>
          <tbody v-if="comparePlansState.loadingPlansToCompare && comparePlansState.openComparisonModal">
            <tr>
              <td :colspan="comparePlansState.plansToCompare.length + 1">
                <LoadingView />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex p-2" :class="{ 'flex-1 flex-row w-full items-end justify-end space-x-4': comparePlansState.openComparisonModal, 'flex-row sm:flex-col items-center justify-center sm:w-52 space-x-4 sm:space-x-0 sm:space-y-4': !comparePlansState.openComparisonModal }">
        <button class="bg-blue flex items-center justify-center h-8 lg:h-12 rounded px-2 py-5 hover:bg-blue-600 hover:font-semibold" :class="{ 'w-20 lg:w-24': !comparePlansState.openComparisonModal, 'w-36 lg:w-40': comparePlansState.openComparisonModal, 'cursor-not-allowed bg-gray-400 hover:bg-gray-500': comparePlansState.plansToCompare.length < 2 }" @click="toggleComparisonModal" :disabled="comparePlansState.plansToCompare.length < 2">
          <span class="text-sm lg:text-md text-white">{{ comparePlansState.openComparisonModal ? 'Change Selection' : 'Compare'}}</span>
        </button>
        <PrimaryButton :rounded="AllowedRoundedSize.BASIC" @click="closeComparison" class="w-20 h-8 lg:w-24 lg:h-12 justify-center items-center">
          Close
        </PrimaryButton>
      </div>
    </div>
  </div>
</template>
